import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ModakBackdrop from "./ModakBackdrop";
import { useRoundGate } from "./useRoundGate";
import useIsNarrow from "./useIsNarrow";

/** Maps each round number to its URL slug, keyed off the stage name (not "/round1" etc). */
const ROUND_PATHS = {
  1: "bhog-a-licious",
  2: "music-o-logy",
  3: "name-a-thon",
  4: "symbols",
  5: "avatar",
};

/** Percentages measured directly off each signpost circle's pixel bounds in journey_desk.png/journey_mob.png — both images already have the "Bhog-a-licious" etc. labels baked in, so these only mark the invisible click target + a done badge, not a duplicate label. */
const STOPS_DESK = [
  { round: 1, label: "Bhog-a-licious", left: 16.11, top: 9.47 },
  { round: 2, label: "Music-o-logy", left: 34.26, top: 17.41 },
  { round: 3, label: "Name-a-thon", left: 49.84, top: 45.26 },
  { round: 4, label: "Symbols", left: 70.89, top: 43.31 },
  { round: 5, label: "Avatar", left: 88.37, top: 22.42 },
];

const STOPS_MOB = [
  { round: 1, label: "Bhog-a-licious", left: 33.8, top: 7.47 },
  { round: 2, label: "Music-o-logy", left: 65.28, top: 21.64 },
  { round: 3, label: "Name-a-thon", left: 36.11, top: 42.28 },
  { round: 4, label: "Symbols", left: 68.98, top: 58.32 },
  { round: 5, label: "Avatar", left: 31.25, top: 87.37 },
];

/** Where the walking mascot marker sits on the road itself (distinct from the signpost position, same as the old PHP app's separate .row/.child coordinates). Mobile just nudges toward the road curve next to each sign. */
const MARKER_POS_DESK = {
  1: { left: 13, top: 29 },
  2: { left: 34, top: 35 },
  3: { left: 48, top: 64 },
  4: { left: 69, top: 64 },
  5: { left: 86, top: 38 },
};

const MARKER_POS_MOB = {
  1: { left: 33.8, top: 15 },
  2: { left: 65.28, top: 29 },
  3: { left: 36.11, top: 50 },
  4: { left: 68.98, top: 66 },
  5: { left: 31.25, top: 95 },
};

export default function Home() {
  const navigate = useNavigate();
  const { loading, status } = useRoundGate(null);
  const narrow = useIsNarrow();

  useEffect(() => {
    document.title = "Modak Marathon";
  }, []);

  const STOPS = narrow ? STOPS_MOB : STOPS_DESK;
  const MARKER_POS = narrow ? MARKER_POS_MOB : MARKER_POS_DESK;
  const roadmapImg = narrow ? "/modak-assets/home/journey_mob.png" : "/modak-assets/home/journey_desk.png";

  const rounds = status?.rounds;
  // Only treat as finished when every round is explicitly done (not on a
  // fresh insert / empty progress). Completed players are steered to thankyou
  // without the old "Thank you for participating!" popup on the game page.
  const allDone =
    !!rounds &&
    [1, 2, 3, 4, 5].every((n) => rounds[n] && rounds[n].done === true);
  const nextRound = useMemo(() => {
    if (!rounds) return 1;
    const firstIncomplete = STOPS.find((s) => !rounds[s.round]?.done);
    return firstIncomplete ? firstIncomplete.round : 5;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds, narrow]);

  useEffect(() => {
    if (!loading && allDone) {
      navigate("/thankyou", { replace: true });
    }
  }, [loading, allDone, navigate]);

  if (loading || !rounds || allDone) return null;

  const markerPos = MARKER_POS[Math.min(nextRound, 5)];
  const mascotImg = `/modak-assets/home/modak${Math.min(nextRound, 5)}.png`;

  const goToRound = (round, done) => {
    if (done) {
      Swal.fire("You have already played.", "", "");
      return;
    }
    if (round !== nextRound) return;
    navigate(`/${ROUND_PATHS[round]}`);
  };

  return (
    <ModakBackdrop fullHeight>
      <h2 className="modak-roadmap-heading">
        Race to the finish line by overcoming roadblocks in your path and earn points to enjoy a colourful ride
      </h2>

      <div className="modak-roadmap-wrap">
        <img className="modak-roadmap-bg" src={roadmapImg} alt="Roadmap" />

        <img
          className="modak-roadmap-marker"
          src={mascotImg}
          alt="Progress"
          style={{ left: `${markerPos.left}%`, top: `${markerPos.top}%` }}
        />

        {STOPS.map((s) => {
          const done = !!rounds[s.round]?.done;
          const playable = s.round === nextRound && !allDone;
          return (
            <div
              key={s.round}
              className={`modak-roadmap-hit${playable ? " is-playable" : ""}`}
              style={{ left: `${s.left}%`, top: `${s.top}%` }}
              onClick={() => goToRound(s.round, done)}
              title={s.label}
            />
          );
        })}
      </div>
    </ModakBackdrop>
  );
}
